import { LibraryPanel } from "./LibraryPanel";
import { AgentConnection } from "./AgentConnection";
import { Modal } from "./Modal";
import { ProjectCard } from "./ProjectCard";
import { UpdatesButton } from "./UpdatesButton";
import { AccountMenu } from "./AccountMenu";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
const AdminConsole = lazy(() =>
  import("./AdminConsole").then(m => ({ default: m.AdminConsole })),
);
import { projectHistory } from "./project-history";


import { ArrowUpRight, Plus, Layers, Library, X } from "lucide-react";

import { api, notify, type User, type Room } from "./api";
import { captureEvent } from "./product-analytics";

import { Brand } from "./Brand";
import { Welcome } from "./Welcome";
import { Board } from "./Board";
export function App({
  managed,
}: {
  managed?: { user: User; signOut: () => Promise<void> };
}) {
  const [user, setUser] = useState<User | null>(managed?.user ?? null),
    [ready, setReady] = useState(Boolean(managed)),
    [signInAvailable, setSignInAvailable] = useState(true),
    [name, setName] = useState(""),
    [rooms, setRooms] = useState<Room[]>([]),
    [room, setRoom] = useState<Room | null>(null),
    [busy, setBusy] = useState(false),
    [newProject, setNewProject] = useState(false),
    [exampleProject, setExampleProject] = useState(false),
    [projectName, setProjectName] = useState(""),
    [toast, setToast] = useState(""),
    [projectView, setProjectView] = useState<"active" | "archived">("active"),
    [deleteProject, setDeleteProject] = useState<Room | null>(null),
    [managementError, setManagementError] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(() =>
    new URLSearchParams(location.search).has("library"),
  );
  const [libraryProjectPicker, setLibraryProjectPicker] = useState(false);
  const [libraryItem, setLibraryItem] = useState<string | undefined>();
  const visibleRooms = rooms.filter(
    (r) => Boolean(r.archivedAt) === (projectView === "archived"),
  );
  const navigation = useRef<ReturnType<typeof projectHistory> | null>(null);
  const opening = useRef(0);
  const manageProject = async (
    project: Room,
    action: "archive" | "restore" | "delete",
  ) => {
    if (busy) return;
    setBusy(true);
    setManagementError("");
    try {
      await api(
        `/api/rooms/${project.id}/${action}`,
        action === "delete" ? { confirm: true } : {},
      );
      setRooms((current) =>
        action === "delete"
          ? current.filter((r) => r.id !== project.id)
          : current.map((r) =>
              r.id === project.id
                ? {
                    ...r,
                    archivedAt:
                      action === "archive" ? new Date().toISOString() : null,
                  }
                : r,
            ),
      );
      setDeleteProject(null);
      notify(
        action === "delete"
          ? "Project deleted."
          : action === "archive"
            ? "Project archived. Find it in Archived."
            : "Project restored.",
      );
    } catch (error) {
      const message = (error as Error).message;
      if (action === "delete") setManagementError(message);
      else notify(message);
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    setRooms(await api<Room[]>("/api/rooms"));
  };
  const open = async (
    id: string,
    thread?: string,
    replace = false,
    textShape?: string,
  ) => {
    const request = ++opening.current;
    setBusy(true);
    try {
      const invite =
        new URLSearchParams(location.hash.slice(1)).get("invite") ??
        new URLSearchParams(location.search).get("invite");
      if (invite) {
        await api(`/api/rooms/${id}/join`, { token: invite });
        captureEvent("invite_accepted");
      }
      const r = await api<Room>(`/api/rooms/${id}`);
      if (request !== opening.current) return;
      setRoom(r);
      void api(`/api/rooms/${id}/opened`, {}).catch(() => {});
      const params = new URLSearchParams(location.search);
      const agentConnect =
        params.get("room") === id && params.get("agentConnect") === "1";
      navigation.current?.write(
        `/app?room=${id}${thread ? `&thread=${encodeURIComponent(thread)}` : ""}${textShape ? `&mentionText=${encodeURIComponent(textShape)}` : ""}${agentConnect ? "&agentConnect=1" : ""}`,
        replace,
      );
    } catch (e) {
      if (request === opening.current) {
        if (replace) {
          navigation.current?.write("/app", true);
          showProjects();
        }
        notify((e as Error).message);
      }
    } finally {
      if (request === opening.current) setBusy(false);
    }
  };
  const showProjects = () => {
    ++opening.current;
    setBusy(false);
    setRoom(null);
    refresh().catch((e) => notify(e.message));
  };
  useEffect(() => {
    const listener = (e: Event) => {
      const { board, thread, textShape } = (e as CustomEvent).detail;
      void open(board, thread, false, textShape);
    };
    window.addEventListener("planroom:open-review", listener);
    return () => window.removeEventListener("planroom:open-review", listener);
  }, []);
  useEffect(() => {
    const listener = (e: Event) => setToast((e as CustomEvent).detail);
    window.addEventListener("planroom:notice", listener);
    if (!managed)
      api<{ user: User | null; signInAvailable?: boolean }>("/api/session")
        .then((r) => {
          setUser(r.user);
          setSignInAvailable(r.signInAvailable !== false);
          setReady(true);
        })
        .catch(() => {
          setReady(true);
          notify("Cannot connect to the workspace server.");
        });
    return () => window.removeEventListener("planroom:notice", listener);
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 5500);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (user) {
      navigation.current = projectHistory(
        window,
        (url) => {
          const id = url.searchParams.get("room");
          if (id)
            void open(id, url.searchParams.get("thread") ?? undefined, true);
          else showProjects();
        },
        () =>
          window.dispatchEvent(
            new Event("planroom:before-navigation", { cancelable: true }),
          ),
        "/app",
      );
      if (!new URLSearchParams(location.search).has("admin"))
        refresh().catch((e) => notify(e.message));
      const id = new URLSearchParams(location.search).get("room");
      if (id)
        open(
          id,
          new URLSearchParams(location.search).get("thread") ?? undefined,
          true,
        );
      return () => {
        ++opening.current;
        navigation.current?.dispose();
        navigation.current = null;
      };
    }
  }, [user?.id]);
  const saveName = async (name: string) => {
    const { user: updated } = await api<{ user: User }>("/api/profile", {
      name,
    });
    setUser(current => current ? { ...current, ...updated } : updated);
    notify("Name updated.");
  };
  const create = async (demo: boolean) => {
    const title = projectName.trim();
    if (busy || !title) return;
    setBusy(true);
    try {
      const r = await api<Room>("/api/rooms", {
        name: title,
        demo,
      });
      captureEvent("project_created", { demo });
      setNewProject(false);
      await open(r.id);
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const agentBoard = new URLSearchParams(location.search);
  if (ready && user && agentBoard.has("admin"))
    return (
      <Suspense fallback={<div className="loading"><Brand /><p>Opening admin…</p></div>}>
        <AdminConsole user={user} />
      </Suspense>
    );
  if (
    user?.features?.agents &&
    agentBoard.get("agentConnect") === "1" &&
    agentBoard.get("room")
  )
    return (
      <AgentConnection
        boardId={agentBoard.get("room")!}
        name={room?.id === agentBoard.get("room") ? room.name : undefined}
      />
    );
  return (
    <>
      {libraryProjectPicker && (
        <Modal
          title={libraryItem ? "Use in a project" : "Open a project to save"}
          close={() => setLibraryProjectPicker(false)}
        >
          <p>Choose a project you can edit.</p>
          <div className="library-project-list">
            {rooms
              .filter(
                (r) => !r.archivedAt && ["owner", "editor"].includes(r.role),
              )
              .map((r) => (
                <button
                  key={r.id}
                  disabled={busy}
                  onClick={async () => {
                    await open(r.id);
                    setLibraryProjectPicker(false);
                  }}
                >
                  <span>{r.name}</span>
                  <ArrowUpRight size={16} />
                </button>
              ))}
          </div>
          {!rooms.some(
            (r) => !r.archivedAt && ["owner", "editor"].includes(r.role),
          ) && <p>Create an editable project first.</p>}
        </Modal>
      )}
      {!ready ? (
        <div className="loading">
          <Brand />
          <p>Opening your workspace…</p>
        </div>
      ) : !user ? (
        <Welcome accessOnly>
          {signInAvailable ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                try {
                  setUser(
                    (await api<{ user: User }>("/api/session", { name })).user,
                  );
                } catch (e) {
                  notify((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <label>
                Your name
                <input
                  placeholder="Your display name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  required
                />
              </label>
              <button className="primary" disabled={busy}>
                {busy ? "Opening your studio…" : "Open your studio"}{" "}
                <ArrowUpRight size={18} />
              </button>
            </form>
          ) : (
            <p role="status">
              This workspace is being prepared. Sign-in will be available soon.
            </p>
          )}
          <small>
            {signInAvailable
              ? "Local workspace · this name appears on your comments and cursor."
              : "Workspace preview · sign-in coming next."}
          </small>
        </Welcome>
      ) : room ? (
        <Board
          key={room.id}
          libraryItem={libraryItem}
          room={room}
          user={user}
          signOut={managed?.signOut}
          saveName={managed ? saveName : undefined}
          back={() => {
            navigation.current?.write("/app");
            setLibraryItem(undefined);
            showProjects();
          }}
        />
      ) : (
        <div className="projects">
          <header>
            <Brand />
            <div className="project-header-actions">
              <button
                aria-pressed={libraryOpen}
                onClick={() => setLibraryOpen(!libraryOpen)}
              >
                <Library size={17} />
                My Library
              </button>
              {managed && <UpdatesButton />}
              {managed ? (
                <AccountMenu
                  user={user}
                  saveName={saveName}
                  signOut={managed.signOut}
                />
              ) : (
                <span className="user-avatar" title={user.name}>
                  {user.name.slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
          </header>
          <main>
            {libraryOpen ? (
              <LibraryPanel
                close={() => setLibraryOpen(false)}
                chooseProject={(id) => {
                  setLibraryItem(id);
                  setLibraryProjectPicker(true);
                }}
              />
            ) : (
              <>
                <div className="project-intro">
                  <div>
                    <span className="eyebrow">YOUR STUDIO</span>
                    <h1>Your projects, on the table.</h1>
                    <p>
                      Open a project to develop your sheets, explore design
                      options, and review with your studio.
                    </p>
                  </div>
                  <button
                    className="primary"
                    disabled={busy}
                    onClick={() => {
                      setProjectName("");
                      setExampleProject(false);
                      setNewProject(true);
                    }}
                  >
                    <Plus size={18} />
                    New project
                  </button>
                </div>
                <div className="section-heading">
                  <div className="project-filters" aria-label="Project filter">
                    <button
                      aria-pressed={projectView === "active"}
                      onClick={() => setProjectView("active")}
                    >
                      Active
                    </button>
                    <button
                      aria-pressed={projectView === "archived"}
                      onClick={() => setProjectView("archived")}
                    >
                      Archived
                    </button>
                  </div>
                  <span>
                    {visibleRooms.length} project
                    {visibleRooms.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="project-grid">
                  {visibleRooms.map((r) => (
                    <ProjectCard
                      key={r.id}
                      room={r}
                      busy={busy}
                      open={() => void open(r.id)}
                      archive={() =>
                        void manageProject(
                          r,
                          r.archivedAt ? "restore" : "archive",
                        )
                      }
                      remove={() => {
                        setManagementError("");
                        setDeleteProject(r);
                      }}
                    />
                  ))}
                  {visibleRooms.length === 0 && (
                    <p className="project-empty">
                      {projectView === "archived"
                        ? "No archived projects. Projects you archive will appear here."
                        : "No active projects. Create a project to get started."}
                    </p>
                  )}
                  {!managed && projectView === "active" && (
                    <button
                      className="example-card"
                      onClick={() => {
                        setProjectName("Courtyard house");
                        setNewProject(true);
                        setExampleProject(true);
                      }}
                      disabled={busy}
                    >
                      <span className="example-icon">
                        <Layers size={28} />
                      </span>
                      <h3>Start with a courtyard house.</h3>
                      <p>
                        Explore the tools with a calibrated example floor plan.
                      </p>
                      <span className="text-link">
                        Open example project <ArrowUpRight size={16} />
                      </span>
                      <small>Synthetic drawing · no real project data</small>
                    </button>
                  )}
                </div>
              </>
            )}
          </main>
          <footer>
            CALQUE <span>Plans, references, and reviews. Together.</span>
            <span>
              {managed
                ? "Private workspace / development"
                : "Local preview / v0.1"}
            </span>
          </footer>
        </div>
      )}
      {newProject && !room && (
        <Modal
          title="New project"
          initialFocus="input"
          close={() => {
            if (!busy) setNewProject(false);
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void create(exampleProject);
            }}
          >
            <label>
              Project name
              <input
                autoFocus
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
                placeholder="e.g. Courtyard renovation"
                maxLength={100}
                required
                disabled={busy}
              />
            </label>
            <div className="dialog-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => setNewProject(false)}
              >
                Cancel
              </button>
              <button
                className="primary"
                disabled={busy || !projectName.trim()}
              >
                {busy ? "Creating…" : "Create project"}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {deleteProject && !room && (
        <Modal
          title="Delete project?"
          initialFocus=".project-delete-cancel"
          close={() => {
            if (!busy) setDeleteProject(null);
          }}
        >
          <p>
            Delete <strong>{deleteProject.name}</strong> for everyone? Its
            sheets, files and comments will no longer be accessible. You cannot
            restore it from the studio.
          </p>
          <p>To keep the project for later, archive it instead.</p>
          {managementError && <p role="alert">{managementError}</p>}
          <div className="dialog-actions">
            <button
              className="project-delete-cancel"
              disabled={busy}
              onClick={() => setDeleteProject(null)}
            >
              Cancel
            </button>
            <button
              className="project-delete-confirm"
              disabled={busy}
              onClick={() => void manageProject(deleteProject, "delete")}
            >
              {busy ? "Deleting…" : "Delete project"}
            </button>
          </div>
        </Modal>
      )}
      {toast && (
        <div className="toast" role="status">
          {toast}
          <button aria-label="Dismiss message" onClick={() => setToast("")}>
            <X size={15} />
          </button>
        </div>
      )}
    </>
  );
}
